/**
 * Completion (autocomplete) contract between a CodeEditorAdapter and the Monaco based CodeEditor.
 *
 * Adapters describe suggestions with plain objects and never touch Monaco; the editor turns them into Monaco
 * completion items (toMonacoCompletions) and registers one provider per editor instance. Monaco providers are
 * global per language, so the registered one answers only for the model of its own editor.
 */

export type CodeCompletionKind = 'function' | 'keyword' | 'variable' | 'field' | 'table' | 'snippet'
	| 'constant' | 'value' | 'text' | 'operator' | 'module';

export type CodeCompletionItem = {
	/** text shown in the suggest list; the typed text is matched against it unless filterText is set */
	label: string,
	kind?: CodeCompletionKind,
	/** short text shown next to the label, e.g. a signature */
	detail?: string,
	/** markdown shown in the details pane of the suggest widget */
	documentation?: string,
	/** text inserted instead of the label; Monaco snippet syntax (`$1`, `${1:name}`, `$0`) when `snippet` is true */
	insertText?: string,
	snippet?: boolean,
	/** what the typed text is matched against instead of the label (e.g. the first word of a multi-word keyword) */
	filterText?: string,
	/** ordering among items with the same match score (defaults to the label) */
	sortText?: string,
};

export type CodeCompletionContext = {
	/** identifier being typed at the cursor ('' when the cursor does not touch one) */
	word: string,
	/** non-identifier characters glued before the word, e.g. '@', '@@', '#' or '{' ('' when none) */
	wordPrefix: string,
	/** text of the current line before the cursor */
	linePrefix: string,
	/** text of the current line after the cursor */
	lineSuffix: string,
	lineNumber: number,
	column: number,
	/** character that opened the suggest widget (one of the provider's triggerCharacters); undefined when typed or invoked */
	triggerCharacter?: string,
	/** whole editor content */
	getContent: () => string,
};

export type CodeCompletionProvider = {
	/** characters that open the suggest widget besides typing an identifier, e.g. ['@', '.'] */
	triggerCharacters?: string[],
	/**
	 * Offer the words found in the document after the provided items (default true). Monaco skips its own word
	 * based suggestions whenever a provider returns items, so without this table and column names already typed
	 * in the document would disappear from the list.
	 */
	documentWords?: boolean,
	provide(context: CodeCompletionContext): CodeCompletionItem[] | Promise<CodeCompletionItem[]>,
};

/** Concatenates the items of several providers; trigger characters are unioned. */
export function mergeCompletionProviders(...providers: CodeCompletionProvider[]): CodeCompletionProvider {
	const list = providers.filter(Boolean);
	const triggerCharacters = [...new Set(list.flatMap(p => p.triggerCharacters ?? []))];
	return {
		triggerCharacters: triggerCharacters.length ? triggerCharacters : undefined,
		documentWords: list.some(p => p.documentWords !== false),
		provide: async context => (await Promise.all(list.map(p => p.provide(context)))).flat(),
	};
}

/** characters that glue to an identifier without being part of a Monaco word (T-SQL variables, temp tables, pseudo-variables) */
const WORD_PREFIX_RE = /[@#$:{]+$/;

export function getWordPrefix(textBeforeWord: string): string {
	return WORD_PREFIX_RE.exec(textBeforeWord)?.[0] ?? '';
}

export type CompletionPosition = { lineNumber: number, column: number };

export type CompletionRange = { startLineNumber: number, startColumn: number, endLineNumber: number, endColumn: number };

/** the part of monaco.editor.ITextModel the request needs, kept structural so neither adapters nor tests need Monaco */
export type CompletionTextModel = {
	getWordUntilPosition(position: CompletionPosition): { word: string, startColumn: number, endColumn: number },
	getLineContent(lineNumber: number): string,
	getValue(): string,
};

/** Builds the adapter context for a completion request and the range the accepted item replaces (the word before the cursor). */
export function createCompletionRequest(model: CompletionTextModel, position: CompletionPosition, triggerCharacter?: string)
	: { context: CodeCompletionContext, range: CompletionRange } {
	const line = model.getLineContent(position.lineNumber);
	const word = model.getWordUntilPosition(position);
	return {
		context: {
			word: word.word,
			wordPrefix: getWordPrefix(line.substring(0, word.startColumn - 1)),
			linePrefix: line.substring(0, position.column - 1),
			lineSuffix: line.substring(position.column - 1),
			lineNumber: position.lineNumber,
			column: position.column,
			triggerCharacter,
			getContent: () => model.getValue(),
		},
		range: {
			startLineNumber: position.lineNumber,
			endLineNumber: position.lineNumber,
			startColumn: word.startColumn,
			endColumn: word.endColumn,
		},
	};
}

const DOCUMENT_WORD_RE = /[\p{L}_][\p{L}\p{N}_]*/gu;
/** documents beyond this size (paged logs and the like) are not scanned for words */
const DOCUMENT_WORDS_MAX_LENGTH = 1_000_000;
export const DOCUMENT_WORD_SORT_PREFIX = '8';

/** Unique words of the content as text items, without the word being typed and the given labels (case-insensitive). */
export function createDocumentWordItems(content: string, currentWord: string, exclude: Iterable<string> = []): CodeCompletionItem[] {
	if (!content || content.length > DOCUMENT_WORDS_MAX_LENGTH) return [];
	const seen = new Set<string>();
	for (const label of exclude) seen.add(label.toLowerCase());
	if (currentWord) seen.add(currentWord.toLowerCase());
	const items: CodeCompletionItem[] = [];
	for (const match of content.matchAll(DOCUMENT_WORD_RE)) {
		const key = match[0].toLowerCase();
		if (seen.has(key)) continue;
		seen.add(key);
		items.push({ label: match[0], kind: 'text', sortText: DOCUMENT_WORD_SORT_PREFIX + key });
	}
	return items;
}

const KIND_NAMES: Record<CodeCompletionKind, string> = {
	function: 'Function',
	keyword: 'Keyword',
	variable: 'Variable',
	field: 'Field',
	table: 'Struct',
	snippet: 'Snippet',
	constant: 'Constant',
	value: 'Value',
	text: 'Text',
	operator: 'Operator',
	module: 'Module',
};

/** the Monaco enums the mapping needs: monaco.languages.CompletionItemKind and CompletionItemInsertTextRule.InsertAsSnippet */
export type MonacoCompletionEnums = { kinds: Record<string, number>, insertAsSnippet: number };

export type MonacoCompletionItem = {
	label: string,
	kind: number,
	detail?: string,
	documentation?: { value: string },
	insertText: string,
	insertTextRules?: number,
	filterText?: string,
	sortText?: string,
	range: CompletionRange,
};

/**
 * Maps adapter items to Monaco completion items. The typed prefix ('@', '#', '{'...) is not part of the Monaco
 * word, so an item that carries it (e.g. '@@ROWCOUNT' after '@@') replaces the prefix as well; a plain item
 * (a document word after '#') keeps the word range and the prefix stays in place.
 */
export function toMonacoCompletions(items: CodeCompletionItem[], wordPrefix: string, range: CompletionRange, enums: MonacoCompletionEnums): MonacoCompletionItem[] {
	const prefixedRange = wordPrefix ? { ...range, startColumn: range.startColumn - wordPrefix.length } : range;
	return items.map(item => ({
		label: item.label,
		kind: enums.kinds[KIND_NAMES[item.kind ?? 'text']] ?? enums.kinds.Text,
		detail: item.detail,
		documentation: item.documentation ? { value: item.documentation } : undefined,
		insertText: item.insertText ?? item.label,
		insertTextRules: item.snippet ? enums.insertAsSnippet : undefined,
		filterText: item.filterText,
		sortText: item.sortText,
		range: wordPrefix && item.label.startsWith(wordPrefix) ? prefixedRange : range,
	}));
}
